import { LocaleLink } from "../i18n/LocaleLink";
import {
  CalendarClock, Navigation,
} from "lucide-react";
import { Container, Eyebrow } from "../components/ui";
import DashboardShowcase from "../components/DashboardShowcaseV2";
import TimeBento from "../components/TimeBento";
import FeatureOverview from "../components/FeatureOverview";
import { FeatureDetailBlock, DETAIL_BLOCKS } from "../components/FeatureDetail";
import PricingPlans from "../components/PricingPlans";
import IndustryCta from "../components/IndustryCta";
import { SiteNav } from "../components/SiteNav";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";
import { featureLiveForMarket } from "../content/flags";
import { sectionVisibleForMarket } from "../content/sections";
import { testimonialsFor } from "../content/testimonials";
import {
  Hero as DSHero, type Review, BentoCard, MotionProvider, Reveal,
  ShieldBadge, Marquee,
  Workflow, type WorkflowStep, Badge as DSBadge, Button as DSButton,
  RegisterPanel, DeadlinePanel, RiskPanel,
  ConnectedSteps, type ConnectedStep,
  Faq, type QA, PromoBanner, SectionHeading,
} from "../ds/core";
import { TRIAL_PATH } from "../config/entrypoints";

/* ---------- Home top — Framer hero (DS core) + our market-aware content ----------
   First section overlay (Epic 3). The old light hero is replaced by the DS-core
   Hero: promo Ticker + overlaid Header + dark hero with our copy + review slider. */
const FRAMER_IMG = "https://framerusercontent.com/images";
const HERO_BG = `${FRAMER_IMG}/gvrZUoowU6t5rwpwtONkkVWoFSU.png`;


function HomeTop() {
  const { market, t, locale } = useLocale();
  const isBelgium = market === "belgium";
  // CMS-managed testimonials for this locale (author + photo + localised role /
  // quote), editable in the admin. Falls back to the baked i18n hero reviews when
  // none are published yet for the locale (avatar defaults to the first stock one).
  const cmsReviews = testimonialsFor(locale);
  const heroReviews: Review[] = cmsReviews.length
    ? cmsReviews.map((r) => ({
        author: r.author,
        role: r.role || undefined,
        quote: r.quote,
        avatar: r.photo || `${FRAMER_IMG}/W8NNLRWRvorI9Yxwz1ruURSapb0.png`,
        alt: t("home:hero.reviews.alt"),
      }))
    : [
        { author: "Jessica L.", role: t("home:hero.reviews.0.role"), quote: t("home:hero.reviews.0.quote"), avatar: cmsImage(t, "home:hero.reviews.0.avatar", `${FRAMER_IMG}/W8NNLRWRvorI9Yxwz1ruURSapb0.png`, 160), alt: t("home:hero.reviews.alt") },
        { author: "Richard", role: t("home:hero.reviews.1.role"), quote: t("home:hero.reviews.1.quote"), avatar: cmsImage(t, "home:hero.reviews.1.avatar", `${FRAMER_IMG}/9TjZCNLj0zeTUT3Swyp6tGoGs3E.png`, 160), alt: t("home:hero.reviews.alt") },
        { author: "Eric", role: t("home:hero.reviews.2.role"), quote: t("home:hero.reviews.2.quote"), avatar: cmsImage(t, "home:hero.reviews.2.avatar", `${FRAMER_IMG}/TOz8DTmae12DdNERCCHPhUfvM.jpg`, 160), alt: t("home:hero.reviews.alt") },
      ];
  return (
    <>
      <SiteNav />
      <DSHero
        eyebrow={isBelgium ? t("home:hero.eyebrow.be") : undefined}
        title={
          isBelgium ? (
            <>
              {t("home:hero.title.be.lead")} <span className="text-ds-accent">{t("home:hero.title.be.accent")}</span>
            </>
          ) : (
            t("home:hero.title.intl")
          )
        }
        paragraph={
          isBelgium
            ? t("home:hero.paragraph.be")
            : t("home:hero.paragraph.intl")
        }
        primaryCta={{ label: t("common:cta.startFreeTrial"), to: TRIAL_PATH }}
        secondaryCta={{ label: t("common:cta.seeHowItWorks"), to: "/features" }}
        reviews={heroReviews}
        bgImage={cmsImage(t, "home:hero.image", HERO_BG, 1920)}
        fallbackNote={
          isBelgium ? (
            <>{t("home:hero.fallbackNote.be")}</>
          ) : undefined
        }
      />
      {/* Mobile only: the web hero image as its own block right after the hero. */}
      <div className="px-4 lg:hidden">
        <img src={cmsImage(t, "home:hero.image", HERO_BG)} alt={t("home:hero.image.alt")} loading="lazy" decoding="async" className="h-64 w-full rounded-3xl object-cover" />
      </div>
    </>
  );
}

/* ---------- Getting started (horizontal connected steps, photo placeholders) ---------- */
// Approved onboarding shots, already uploaded to Cloudinary under their CMS
// slug. Baked as fallbacks so the three steps show distinct, step-relevant
// photos everywhere (local dev never runs cms:fetch) — the CMS still overrides.
const ONBOARD_IMG = [
  "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784550151/home/onboard-steps-0-image.jpg",
  "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784550153/home/onboard-steps-1-image.jpg",
  "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784550154/home/onboard-steps-2-image.jpg",
];
function OnboardSteps() {
  const { t } = useLocale();
  const steps: ConnectedStep[] = [
    { title: t("home:onboard.steps.0.title"), body: t("home:onboard.steps.0.body"), image: cmsImage(t, "home:onboard.steps.0.image", ONBOARD_IMG[0]), alt: t("home:onboard.steps.alt") },
    { title: t("home:onboard.steps.1.title"), body: t("home:onboard.steps.1.body"), image: cmsImage(t, "home:onboard.steps.1.image", ONBOARD_IMG[1]), alt: t("home:onboard.steps.alt") },
    { title: t("home:onboard.steps.2.title"), body: t("home:onboard.steps.2.body"), image: cmsImage(t, "home:onboard.steps.2.image", ONBOARD_IMG[2]), alt: t("home:onboard.steps.alt") },
  ];
  return (
    <ConnectedSteps
      eyebrow={<Eyebrow>{t("home:onboard.eyebrow")}</Eyebrow>}
      title={<>{t("home:onboard.title.lead")} <span className="text-ds-accent">{t("home:onboard.title.accent")}</span></>}
      description={t("home:onboard.description")}
      actions={
        <>
          <DSButton to={TRIAL_PATH} variant="solid">{t("home:onboard.cta.primary")}</DSButton>
          <DSButton to="/getting-started" variant="outline">{t("home:onboard.cta.secondary")}</DSButton>
        </>
      }
      steps={steps}
    />
  );
}

function WorkflowSection() {
  const { t } = useLocale();
  const steps: WorkflowStep[] = [
    { n: "01", title: t("home:workflow.steps.0.title"), body: t("home:workflow.steps.0.body"), media: <RegisterPanel /> },
    { n: "02", title: t("home:workflow.steps.1.title"), body: t("home:workflow.steps.1.body"), media: <DeadlinePanel /> },
    { n: "03", title: t("home:workflow.steps.2.title"), body: t("home:workflow.steps.2.body"), media: <RiskPanel badge={t("home:workflow.steps.2.badge")} /> },
  ];
  return (
    <Workflow
      eyebrow={<DSBadge><CalendarClock size={14} strokeWidth={2} /> {t("home:workflow.eyebrow")}</DSBadge>}
      title={<>{t("home:workflow.title.lead")} <span className="text-ds-ink">{t("home:workflow.title.accent")}</span></>}
      description={t("home:workflow.description")}
      steps={steps}
      cta={{
        title: t("home:workflow.cta.title"),
        body: t("home:workflow.cta.body"),
        action: <DSButton to="/law-2027" variant="outline">{t("home:workflow.cta.action")}</DSButton>,
      }}
    />
  );
}

/* ---------- Value band ---------- */

/** Trip-type mileage breakdown for the Less-admin "Trips" donut. Labels are i18n
  segments: { value: number; color: string }[]; size?: number; thickness?: number; gapDeg?: number; center?: ReactNode;
}) {
  const c = size / 2, rOut = c - 1, rIn = c - 1 - thickness;
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const polar = (r: number, d: number) => { const a = ((d - 90) * Math.PI) / 180; return [c + r * Math.cos(a), c + r * Math.sin(a)]; };
  let cur = 0;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((s, i) => {
          const a0 = cur + gapDeg / 2, a1 = cur + (s.value / total) * 360 - gapDeg / 2; cur += (s.value / total) * 360;
          const [osx, osy] = polar(rOut, a1), [oex, oey] = polar(rOut, a0), [isx, isy] = polar(rIn, a0), [iex, iey] = polar(rIn, a1);
          const large = a1 - a0 > 180 ? 1 : 0;
          return <path key={i} d={`M${osx},${osy} A${rOut},${rOut} 0 ${large} 0 ${oex},${oey} L${isx},${isy} A${rIn},${rIn} 0 ${large} 1 ${iex},${iey} Z`} fill={s.color} />;
        })}
      </svg>
      {center && <div className="absolute inset-0 grid place-items-center">{center}</div>}
    </div>
  );
}

/* ---------- Benefits bento — the "From legal obligation…" content as a bento ----------
   The three original cards (compliant / live overview / less admin) are kept; a dark
   statement card + a smart-reminders card are added around them. */
/**
 * Live overview — the app's own panel, one screenshot per interface language.
 * Cropped on delivery to the white panel only: the blue card, its title and
 * subtitle are rendered by the BentoCard around it, so the screenshot's own blue
 * frame would double up. UI ships in nl / fr / en; every other locale gets en.
 */
const LIVE_BOARDS = {
  nl: "https://res.cloudinary.com/lbx0aw1r/image/upload/c_crop,x_99,y_238,w_1321,h_684/f_auto,q_auto,w_1200/live_dutch_aw67hw.png",
  "fr-be": "https://res.cloudinary.com/lbx0aw1r/image/upload/c_crop,x_76,y_232,w_1375,h_717/f_auto,q_auto,w_1200/live_french_o7kah2.png",
  en: "https://res.cloudinary.com/lbx0aw1r/image/upload/c_crop,x_70,y_220,w_1396,h_742/f_auto,q_auto,w_1200/live_english_km0uyy.png",
} as const;

/** Less admin — the app's Trips and Overtime widgets. */
const LESS_ADMIN = {
  nl: "https://res.cloudinary.com/lbx0aw1r/image/upload/f_auto,q_auto,w_1000/v1784708649/less_admin_dutch_1_gr2htw.png",
  "fr-be": "https://res.cloudinary.com/lbx0aw1r/image/upload/f_auto,q_auto,w_1000/v1784708650/less_admin_french_1_d0pr4b.png",
  en: "https://res.cloudinary.com/lbx0aw1r/image/upload/f_auto,q_auto,w_1000/v1784708650/less_admin_english_1_orbxuh.png",
} as const;

/** Track & Trace — the vehicle's route on the map, coloured by speed. */
const TRACK_MAPS = {
  nl: "https://res.cloudinary.com/lbx0aw1r/image/upload/f_auto,q_auto,w_1000/v1784708650/Track_and_trace_dutch_1_mgwqrg.png",
  "fr-be": "https://res.cloudinary.com/lbx0aw1r/image/upload/f_auto,q_auto,w_1000/v1784708650/track_and_trace_french_1_vzqlsg.png",
  en: "https://res.cloudinary.com/lbx0aw1r/image/upload/f_auto,q_auto,w_1000/v1784708650/track_and_trace_english_1_ft2l8j.png",
} as const;

function ReframeBand() {
  const { market, locale, t } = useLocale();
  const isBelgium = market === "belgium";
  const c = isBelgium
    ? {
        eyebrow: t("home:reframe.eyebrow.be"),
        photoLine: t("home:reframe.photoLine.be"),
        compliant: { title: t("home:reframe.compliant.title.be"), body: t("home:reframe.compliant.body.be"), pills: [t("home:reframe.compliant.pills.be.0"), t("home:reframe.compliant.pills.be.1"), t("home:reframe.compliant.pills.be.2")] },
        overview: { title: t("home:reframe.overview.title.be"), body: t("home:reframe.overview.body.be") },
        lessAdmin: { title: t("home:reframe.lessAdmin.title"), body: t("home:reframe.lessAdmin.body") },
      }
    : {
        eyebrow: t("home:reframe.eyebrow.intl"),
        photoLine: t("home:reframe.photoLine.intl"),
        compliant: { title: t("home:reframe.compliant.title.intl"), body: t("home:reframe.compliant.body.intl"), pills: [t("home:reframe.compliant.pills.intl.0"), t("home:reframe.compliant.pills.intl.1"), t("home:reframe.compliant.pills.intl.2")] },
        overview: { title: t("home:reframe.overview.title.intl"), body: t("home:reframe.overview.body.intl") },
        lessAdmin: { title: t("home:reframe.lessAdmin.title"), body: t("home:reframe.lessAdmin.body") },
      };

  const WORKER = `${FRAMER_IMG}/gvrZUoowU6t5rwpwtONkkVWoFSU.png`;
  const cardBody = "mt-2 text-ds-14 leading-relaxed text-ds-ink-soft";

  return (
    <section className="bg-bg font-ds leading-[1.24]">
      <Container className="py-14 md:py-20">
        <Eyebrow>{c.eyebrow}</Eyebrow>
        <MotionProvider>
          <div className="mt-8 grid grid-cols-1 gap-3 lg:grid-cols-3">

            {/* LEFT — compliant + live (top), less-admin (wide, bottom) */}
            <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:col-span-2 lg:auto-rows-[minmax(248px,auto)]">

              {/* Compliant — dimensional shield + pills */}
              <Reveal>
                <BentoCard title={c.compliant.title}>
                  <p className={cardBody}>{c.compliant.body}</p>
                  <div className="flex flex-1 items-center justify-start py-2">
                    <ShieldBadge />
                  </div>
                  <Marquee className="-mx-7">
                    {c.compliant.pills.map((p) => (
                      <span key={p} className="whitespace-nowrap rounded-full border border-line bg-white px-3 py-1.5 text-ds-14 font-medium text-ds-ink-soft shadow-sm">{p}</span>
                    ))}
                  </Marquee>
                </BentoCard>
              </Reveal>

              {/* Live overview — floating glass chart on a sky gradient */}
              <Reveal delay={0.06}>
                <BentoCard className="bg-ds-accent">
                  <h3 className="text-ds-22 font-semibold tracking-tight text-white">{c.overview.title}</h3>
                  <p className="mt-2 text-ds-14 leading-relaxed text-white/85">{c.overview.body}</p>
                  <div className="mt-4 flex flex-1 items-end">
                    {/* The app's own Live overview panel — real screenshot, cropped
                        to the white panel only (the blue card around it is this
                        BentoCard). Dutch / French / English builds of the UI. */}
                    <img
                      src={LIVE_BOARDS[locale as keyof typeof LIVE_BOARDS] ?? LIVE_BOARDS.en}
                      alt={t("home:reframe.overview.alt")}
                      width={1200}
                      height={637}
                      loading="lazy"
                      decoding="async"
                      className="w-full rounded-2xl border border-white/60 shadow-[0_18px_40px_-18px_rgba(15,23,42,0.4)]"
                    />
                  </div>
                </BentoCard>
              </Reveal>

              {/* Less admin — WIDE: donut + floating stat cards */}
              <Reveal delay={0.12} className="sm:col-span-2">
                <BentoCard className="sm:flex-row sm:items-center sm:gap-8">
                  <div className="sm:max-w-[270px]">
                    <h3 className="text-ds-22 font-semibold tracking-tight text-ds-ink">{c.lessAdmin.title}</h3>
                    <p className="mt-2 text-ds-14 leading-relaxed text-ds-ink-soft">{c.lessAdmin.body}</p>
                  </div>
                  {/* The app's own Trips and Overtime widgets — real screenshot,
                      cropped to the widgets only (this card renders the title). */}
                  <img
                    src={LESS_ADMIN[locale as keyof typeof LESS_ADMIN] ?? LESS_ADMIN.en}
                    alt={t("home:reframe.lessAdmin.alt")}
                    width={1000}
                    height={826}
                    loading="lazy"
                    decoding="async"
                    className="mt-5 w-full max-w-[340px] rounded-2xl sm:mt-0"
                  />
                </BentoCard>
              </Reveal>
            </div>

            {/* RIGHT — worker photo (compact) + Track & Trace (the taller card) */}
            <div className="flex min-w-0 flex-col gap-3">
              {/* Worker photo card — takes whatever height Track & Trace leaves, so the
                  column ends level with the taller one beside it. Min height keeps
                  it from collapsing when the row is short. */}
              <Reveal delay={0.1} className="flex min-h-[220px] flex-1">
                <div className="group relative w-full overflow-hidden rounded-3xl border-8 border-white shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
                  <img src={cmsImage(t, "home:reframe.photo", WORKER)} alt={t("home:reframe.photo.alt")} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                </div>
              </Reveal>

              {/* Track & Trace (replaces the invented smart-alerts — client: "this
                  is not in our app"): the vehicle's route drawn on the map by speed. */}
              <Reveal delay={0.16} className="flex">
                <BentoCard className="flex w-full flex-col">
                  <div className="flex items-center gap-2">
                    <Navigation size={16} strokeWidth={2.2} className="text-ds-accent" />
                    <h3 className="text-ds-18 font-semibold tracking-tight text-ds-ink">{t("home:reframe.track.title")}</h3>
                  </div>
                  <p className="mt-1.5 text-ds-14 leading-relaxed text-ds-ink-soft">{t("home:reframe.track.body")}</p>
                  {/* The route map from the app, coloured by speed. */}
                  <img
                    src={TRACK_MAPS[locale as keyof typeof TRACK_MAPS] ?? TRACK_MAPS.en}
                    alt={t("home:reframe.track.alt")}
                    width={1000}
                    height={526}
                    loading="lazy"
                    decoding="async"
                    className="mt-3 w-full rounded-xl"
                  />
                </BentoCard>
              </Reveal>
            </div>

          </div>
        </MotionProvider>
      </Container>
    </section>
  );
}

/* ---------- Project teaser — centred gradient promo block ---------- */
function ProjectTeaser() {
  const { t } = useLocale();
  return (
    <PromoBanner
      // Client's ask: label this block with the sectors Project targets — the
      // rest of the site now speaks to shops and offices too.
      tag={t("home:projectTeaser.sectors")}
      eyebrow={t("home:projectTeaser.eyebrow")}
      title={t("home:projectTeaser.title")}
      body={t("home:projectTeaser.body")}
      action={<DSButton to="/project-management" variant="outline">{t("home:projectTeaser.action")}</DSButton>}
    />
  );
}

/* ---------- Transition callout (need-trigger — signals you've outgrown basic time) ---------- */
function TransitionCallout() {
  const { market, t } = useLocale();
  // "check-in/out" here is a generic symptom, not the BE-only checkin@work feature.
  const needs = market === "belgium"
    ? [t("home:transition.needs.be.0"), t("home:transition.needs.be.1"), t("home:transition.needs.be.2"), t("home:transition.needs.be.3")]
    : [t("home:transition.needs.intl.0"), t("home:transition.needs.intl.1"), t("home:transition.needs.intl.2"), t("home:transition.needs.intl.3")];
  return (
    <PromoBanner
      title={t("home:transition.title")}
      body={<>{t("home:transition.body", { needs: needs.join(", ") })}</>}
      action={<DSButton to="/project-management" variant="outline">{t("home:transition.action")}</DSButton>}
    />
  );
}

/* ---------- Feature detail — Time + Vehicles only (Project block lives on its own page) ---------- */
function FeaturesDetail() {
  const { t } = useLocale();
  return (
    <section id="in-detail" className="scroll-mt-20 bg-surface font-ds">
      <div className="mx-auto max-w-content px-4 py-16 md:px-10 md:py-20">
        <div className="max-w-2xl">
          <SectionHeading eyebrow={t("home:featuresDetail.eyebrow")} title={t("home:featuresDetail.title")} />
          <p className="mt-4 text-ds-18 leading-relaxed text-ds-ink-soft">{t("home:featuresDetail.lead")}</p>
        </div>
        <div className="mt-12 flex flex-col gap-10">
          <FeatureDetailBlock block={DETAIL_BLOCKS[0]} />
          {/* Big editorial photo between the two feature stories */}
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl border border-line bg-bg-subtle shadow-[0_28px_64px_-28px_rgba(28,25,23,0.22)] sm:aspect-[16/10] md:aspect-[16/9]">
            <img
              src={cmsImage(t, "home:featuresDetail.image", "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&auto=format&fit=crop&q=80")}
              alt={t("home:featuresDetail.image.alt")}
              loading="lazy" decoding="async"
              className="absolute inset-0 h-full w-full object-cover object-[center_30%]"
            />
          </div>
          <FeatureDetailBlock block={DETAIL_BLOCKS[1]} />
        </div>
      </div>
    </section>
  );
}

/* ---------- Pricing — new pricing section (PricingPlans): title over the
   month/year switcher, Time (animated) + Project families, single source. ---------- */
function PricingPreview() {
  const { t } = useLocale();
  return (
    <section className="bg-bg-subtle font-ds">
      <PricingPlans
        eyebrow={<Eyebrow>{t("home:pricing.eyebrow")}</Eyebrow>}
        title={t("home:pricing.title")}
        subtitle={t("home:pricing.subtitle")}
      />
      <div className="mx-auto -mt-6 max-w-content px-4 pb-16 md:px-10 md:pb-20">
        <p className="text-center text-ds-14 text-ds-ink-soft">{t("home:pricing.fullDetails.lead")} <LocaleLink to="/pricing" className="font-medium text-ds-accent hover:underline">{t("home:pricing.fullDetails.link")}</LocaleLink>.</p>
      </div>
    </section>
  );
}

function FaqTeaser() {
  const { market, t } = useLocale();
  const qs: QA[] = [
    { q: t("home:faq.q0.q"), a: t("home:faq.q0.a") },
    { q: t("home:faq.q1.q"), a: market === "belgium"
      ? t("home:faq.q1.a.be")
      : t("home:faq.q1.a.intl") },
    market === "belgium"
      ? { q: t("home:faq.q2.be.q"), a: t("home:faq.q2.be.a") }
      : { q: t("home:faq.q2.intl.q"), a: t("home:faq.q2.intl.a") },
    { q: t("home:faq.q3.q"), a: t("home:faq.q3.a") },
    { q: t("home:faq.q4.q"), a: t("home:faq.q4.a") },
    { q: t("home:faq.q5.q"), a: market === "belgium"
      ? t("home:faq.q5.a.be")
      : t("home:faq.q5.a.intl") },
    { q: t("home:faq.q6.q"), a: t("home:faq.q6.a") },
  ];
  return (
    <Faq
      eyebrow={<Eyebrow>{t("home:faq.eyebrow")}</Eyebrow>}
      title={t("home:faq.title")}
      intro={t("home:faq.intro")}
      action={<DSButton to="/faq" variant="outline">{t("home:faq.action")}</DSButton>}
      items={qs}
    />
  );
}

/* ---------- Final CTA — scattered industry-icon grid (see IndustryCta) ---------- */

export default function HomeNew() {
  const { market } = useLocale();
  // Per-market section show/hide baked from the CMS (DIT-335 GG2). Subtractive:
  // a section renders unless an editor hid it for this market. Structural blocks
  // (hero/nav, showcases) are always on — only content-backed sections gate.
  const show = (section: string) => sectionVisibleForMarket(market, "home", section);
  return (
    <>
      <HomeTop />
      <DashboardShowcase />
      {show("reframe") && <ReframeBand />}
      {show("workflow") && market === "belgium" && featureLiveForMarket(market, "law_2027") && <WorkflowSection />}
      {show("onboard") && <OnboardSteps />}
      {show("timeBento") && <TimeBento />}
      {show("projectTeaser") && <ProjectTeaser />}
      {show("featuresDetail") && <FeaturesDetail />}
      <FeatureOverview detailHref="/features" compact />
      {show("pricing") && <PricingPreview />}
      {show("faq") && <FaqTeaser />}
      {/* Second Project CTA — pulled up tight under the FAQ (~24px gap) */}
      {show("transition") && (
        <div className="-mt-24 md:-mt-28 lg:-mt-[132px]">
          <TransitionCallout />
        </div>
      )}
      {show("industryCta") && <IndustryCta />}
    </>
  );
}

